import React from "react";
import { Card, Divider, Row, Col, Tag, Typography } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import {
    selectEmployeeAttendanceSummary,
    selectSelectedDate,
} from "../attendanceOverviewSelectors";

const { Text } = Typography;

const AttendanceDaySummary = () => {
    const summary = useSelector(selectEmployeeAttendanceSummary);
    const selectedDate = useSelector(selectSelectedDate);

    if (!summary) return null;

    const attendance =
        summary.attendance
            ?.replaceAll("_", " ")
            .replace(/\b\w/g, c => c.toUpperCase()) || "Not Marked";

    const colorMap = {
        present: "green",
        absent: "red",
        leave: "orange",
        holiday: "blue",
        compensatory_off: "purple",
    };

    const displayDate = dayjs(selectedDate || dayjs());
    const isToday = displayDate.isSame(dayjs(), "day");

    const hasValue = (value) =>
        value !== null &&
        value !== undefined &&
        String(value).trim() !== "";

    const rows = [];

    if (hasValue(summary.check_in)) {
        rows.push(
            <React.Fragment key="check_in">
                <Col span={10}>
                    <Text type="secondary">Check In</Text>
                </Col>
                <Col span={14}>
                    <Text>{summary.check_in}</Text>
                </Col>
            </React.Fragment>
        );
    }

    if (hasValue(summary.check_out)) {
        rows.push(
            <React.Fragment key="check_out">
                <Col span={10}>
                    <Text type="secondary">Check Out</Text>
                </Col>
                <Col span={14}>
                    <Text>{summary.check_out}</Text>
                </Col>
            </React.Fragment>
        );
    }

    if (hasValue(summary.shift_start) || hasValue(summary.shift_end)) {
        rows.push(
            <React.Fragment key="shift">
                <Col span={10}>
                    <Text type="secondary">Shift</Text>
                </Col>
                <Col span={14}>
                    <Text>
                        {summary.shift_start || "--"}
                        {" - "}
                        {summary.shift_end || "--"}
                    </Text>
                </Col>
            </React.Fragment>
        );
    }

    if (hasValue(summary.work_mode)) {
        rows.push(
            <React.Fragment key="work_mode">
                <Col span={10}>
                    <Text type="secondary">Work Mode</Text>
                </Col>
                <Col span={14}>
                    <Tag color="blue">
                        {summary.work_mode}
                    </Tag>
                </Col>
            </React.Fragment>
        );
    }

    if (hasValue(summary.leave_portion)) {
        rows.push(
            <React.Fragment key="leave_portion">
                <Col span={10}>
                    <Text type="secondary">Leave Portion</Text>
                </Col>
                <Col span={14}>
                    <Text>
                        {summary.leave_portion.replaceAll("_", " ")}
                    </Text>
                </Col>
            </React.Fragment>
        );
    }

    return (
        <Card
            className="selected-summary-card"
            size="small"
            bodyStyle={{ padding: "14px 16px" }}
            style={{
                marginTop: 18,
                borderRadius: 8,
            }}
        >
            <Text strong style={{ fontSize: 15 }}>
                📅 {displayDate.format("dddd, DD MMM YYYY")}
                {isToday ? " (Today)" : ""}
            </Text>

            <Divider style={{ margin: "10px 0 14px" }} />

            <Row gutter={[0, 12]}>
                {/* Status is always meaningful */}
                <Col span={10}>
                    <Text type="secondary">Status</Text>
                </Col>

                <Col span={14}>
                    <Tag color={colorMap[summary.attendance] || "default"}>
                        {attendance}
                        {summary.status ? ` (${summary.status})` : ""}
                    </Tag>
                </Col>

                {/* Only populated fields */}
                {rows}
            </Row>

            {/* Only show Notes when there is actually a note */}
            {hasValue(summary.notes) && (
                <>
                    <Divider style={{ margin: "14px 0 10px" }} />

                    <Text strong>Notes</Text>

                    <div style={{ marginTop: 6 }}>
                        <Text type="secondary">
                            {summary.notes}
                        </Text>
                    </div>
                </>
            )}
        </Card>
    );
};

export default AttendanceDaySummary;